import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { takeUntil, tap } from 'rxjs';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { GroupService } from './../../../core/services/group.service';
import { isStateModeGroup } from '../../../shared/util/state-mode.util';
import { SupportService } from './../../../core/services/support.service';
import { SubscriptionService } from './../../../core/services/subscription.service';
import { TokenUsageService } from './../../../core/services/token-usage.service';
import { UserService } from './../../../core/services/user.service';
import { environment } from './../../../../environments/environment';
import { CONNECT_ROLE_OPTIONS, needsPaystackMigration } from './connect-enable.util';

@Component({
  selector: 'app-subscription-settings',
  templateUrl: './subscription-settings.component.html',
  styleUrls: ['./subscription-settings.component.scss']
})
export class SubscriptionSettingsComponent extends BaseComponent {
  @Input() isNewSubscription: boolean = false;
  processingSubscription: boolean = false;
  @ViewChild('manageSubscriptionDialog') manageSubscriptionDialog?: TemplateRef<any>;
  @ViewChild('tokenPurchaseDialog') tokenPurchaseDialog?: TemplateRef<any>;
  @ViewChild('textMessageCreditPurchaseDialog') textMessageCreditPurchaseDialog?: TemplateRef<any>;
  @ViewChild('autoRechargeDialog') autoRechargeDialog?: TemplateRef<any>;
  @ViewChild('textMessageAutoRechargeDialog') textMessageAutoRechargeDialog?: TemplateRef<any>;
  @ViewChild('invoiceDetailsDialog') invoiceDetailsDialog?: TemplateRef<any>;
  @ViewChild('onboardingDialog') onboardingDialog?: TemplateRef<any>;
  @ViewChild('connectEnableDialog') connectEnableDialog?: TemplateRef<any>;

  supportRequested: boolean = false;

  /** Connect enable: list of group members (with userId) for the dialog */
  connectEnableUserList: any[] = [];
  /** Connect enable: set of userIds to grant access (owner always included) */
  connectEnableSelectedIds: Set<string> = new Set();
  connectEnableLoading: boolean = false;
  connectEnableSubmitting: boolean = false;
  /** Share clinical subscription with Connect (billing to same card) */
  connectShareSubscription: boolean = false;
  /** When sharing: day of month (1-28) that Connect should bill the clinical subscription card. */
  connectBillingDayOfMonth: number = 1;
  /** Per-user Connect role selection (userId -> UserRole value on switching platform) */
  connectUserRoles: Record<string, string> = {};
  /** Connect role options exposed in the dialog. Owner is auto-assigned; era_processor/super_admin are platform-internal. */
  readonly connectRoleOptions = CONNECT_ROLE_OPTIONS;
  requestingSupport: boolean = false;
  openingWhatsAppSupport: boolean = false;

  get connectFeatureEnabled(): boolean {
    return environment.connectEnabled;
  }

  get user(): any {
    return this.userService?.user;
  }

  subscription?: any;

  subscriptionStatus?: string;
  tokenBalance?: number;
  textMessageCreditBalance?: number;
  autoRechargeStatus?: string;
  textMessageAutoRechargeStatus?: string;
  group?: any;

  /** A state unit (tasks/state-mode.md): no MedicAI Connect and no text messages, so neither is offered here. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.group ?? this.groupService.group);
  }
  isPremium: boolean = false;
  selectedSubscriptionTier: 'standard' | 'premium' = 'standard';

  tokenPurchase: any = { amount: 100 };
  textMessageCreditPurchase: any = { amount: 100 };
  autoRecharge: any = { enabled: false, threshold: 0, amount: 0 };
  textMessageAutoRecharge: any = { enabled: false, threshold: 0, amount: 0 };
  invoiceDetails: any = { invoiceName: '', invoiceAddress: '', taxNumber: '', invoiceEmail: '' };
  promoCode: string = '';
  promoCodeDetails: any = null;
  validatingPromoCode: boolean = false;
  promoCodeError: boolean = false;

  /**
   * Determines if promo code field should be shown
   * Promo codes are only valid for new subscriptions, not resubscriptions
   */
  get shouldShowPromoCode(): boolean {
    // Hide promo code if there's an existing subscription record (even if cancelled)
    // Promo codes should only apply to brand new subscriptions
    return !this.subscription;
  }

  /**
   * Get current subscription tier
   */
  get currentTier(): 'standard' | 'premium' {
    return this.isPremium ? 'premium' : 'standard';
  }

  /**
   * Human-readable label for the card on file (Paystack only — PayFast subscriptions
   * predate stored card metadata). Returns null when nothing should be rendered.
   */
  get cardOnFileLabel(): string | null {
    const auth = this.subscription?.authorization;
    if (!auth) return null;
    const brand = (auth.card_type || auth.brand || 'Card').toString();
    const last4 = auth.last4 || '••••';
    const brandLabel = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
    const exp = auth.exp_month && auth.exp_year
      ? ` — exp ${auth.exp_month}/${String(auth.exp_year).slice(-2)}`
      : '';
    return `${brandLabel} ending ${last4}${exp}`;
  }

  expiringMessage?: string;

  invoices?: any;
  invoicesLoaded: boolean = false;


  constructor(
    private subscriptionService: SubscriptionService,
    private usageService: TokenUsageService,
    private userService: UserService,
    private groupService: GroupService,
    private supportService: SupportService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        if (selectedGroup) {
          this.group = selectedGroup;
          this.isPremium = selectedGroup.isPremium === true;
          this.invoiceDetails = { invoiceName: selectedGroup.invoiceName || selectedGroup.name, invoiceAddress: selectedGroup.invoiceAddress || '', taxNumber: selectedGroup.taxNumber || '', invoiceEmail: selectedGroup.invoiceEmail || '' }
          this.subscriptionStatus = selectedGroup.subscriptionStatus;
          this.autoRechargeStatus = selectedGroup.autoRechargeAmount === '0' ? 'DISABLED' : 'ENABLED';
          this.textMessageAutoRechargeStatus = selectedGroup.textMessageAutoRechargeAmount === 0 ? 'DISABLED' : 'ENABLED';
          this.autoRecharge = { enabled: this.autoRechargeStatus === 'ENABLED', threshold: Number(selectedGroup.autoRechargeThreshold), amount: Number(selectedGroup.autoRechargeAmount) }
          this.textMessageAutoRecharge = { enabled: this.textMessageAutoRechargeStatus === 'ENABLED', threshold: Number(selectedGroup.textMessageAutoRechargeThreshold), amount: Number(selectedGroup.textMessageAutoRechargeAmount) }
          // Reset invoices loaded flag when group changes
          this.invoicesLoaded = false;
          this.invoices = null;
        }
      });

    this.subscriptionService.subscription$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result) {
          this.subscription = result;
          if (result.status === 'CANCELLED' && moment(result.nextBillingDate).isSameOrAfter(moment(), 'day')) {
            this.expiringMessage = `You will lose access on ${moment(result.nextBillingDate).format('DD/MM/YYYY')}`;
          } else {
            this.expiringMessage = '';
          }
        }
      });

    this.subscriptionService.invoices$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.invoices = result;
      });

    this.usageService.tokenBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.tokenBalance = result;
      });


    this.usageService.textMessageCreditBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.textMessageCreditBalance = result;
      });

    this.groupService.updateCardDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe(updateCardDetails => {
        if (updateCardDetails) {
          setTimeout(() => {
            this.openManageSubscriptionDialog();
            this.groupService.updateCardDetails.next(false);
          }, 50)
        }
      });
  }

  openManageSubscriptionDialog() {
    this.dialog.open(this.manageSubscriptionDialog!, { width: '400px', autoFocus: false });
  }

  openOnboardingDialog() {
    this.dialog.open(this.onboardingDialog!, { 
      width: '600px', 
      autoFocus: false,
      disableClose: false
    });
  }

  /** Map clinical GroupUser.userRole + owner status to a default Connect UserRole. */
  private defaultConnectRoleFor(user: { userId: string; userRole?: string }): string {
    if (this.group?.ownerId === user.userId) return 'tenant_admin';
    switch (user.userRole) {
      case 'Admin': return 'tenant_admin';
      case 'Physician': return 'provider';
      case 'Support-Unlimited': return 'billing_manager';
      case 'Support-Limited':
      default: return 'viewer';
    }
  }

  /** Enable Connect: move the subscription to Paystack first if needed, then show the provisioning dialog */
  async startConnectEnable() {
    if (!this.group) return;
    if (!needsPaystackMigration(this.subscription)) {
      this.openConnectEnableDialog();
      return;
    }
    const confirmed = await this.showConfirmationDialog(
      'Step 1: update your card',
      'MedicAI Connect needs your subscription on our new payment provider, Paystack. Update your card now to continue to the Connect setup.',
    );
    if (confirmed) {
      this.updateCardDetails(() => this.openConnectEnableDialog());
    }
  }

  /** Open Enable Connect dialog: load group users, owner pre-selected and disabled */
  openConnectEnableDialog() {
    if (!this.group) return;
    this.connectEnableLoading = true;
    this.connectEnableUserList = [];
    this.connectEnableSelectedIds = new Set();
    this.connectUserRoles = {};
    // Pre-fill billing day from existing subscription's nextBillingDate; clamp to [1, 28]. Fallback: 1.
    const nbd = this.subscription?.nextBillingDate ? moment(this.subscription.nextBillingDate) : null;
    this.connectBillingDayOfMonth = nbd && nbd.isValid()
      ? Math.min(28, Math.max(1, nbd.date()))
      : 1;
    this.dialog.open(this.connectEnableDialog!, { width: '600px', maxHeight: '90vh', autoFocus: false });
    this.groupService.getGroupUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const users = response?.users ?? [];
          this.connectEnableUserList = users.filter((u: any) => u.userId);
          const ownerId = this.group?.ownerId;
          this.connectEnableSelectedIds = new Set(
            ownerId ? [ownerId, ...this.connectEnableUserList.map((u: any) => u.userId)] : this.connectEnableUserList.map((u: any) => u.userId)
          );
          const roles: Record<string, string> = {};
          for (const u of this.connectEnableUserList) {
            roles[u.userId] = this.defaultConnectRoleFor(u);
          }
          this.connectUserRoles = roles;
          this.connectEnableLoading = false;
        },
        error: () => {
          this.connectEnableLoading = false;
          this.showToastMessage('Failed to load group members.');
        }
      });
  }

  isConnectEnableOwner(userId: string): boolean {
    return this.group?.ownerId === userId;
  }

  isConnectEnableSelected(userId: string): boolean {
    return this.connectEnableSelectedIds.has(userId);
  }

  toggleConnectEnableUser(userId: string) {
    if (this.group?.ownerId === userId) return;
    if (this.connectEnableSelectedIds.has(userId)) {
      this.connectEnableSelectedIds.delete(userId);
    } else {
      this.connectEnableSelectedIds.add(userId);
    }
    this.connectEnableSelectedIds = new Set(this.connectEnableSelectedIds);
  }

  confirmConnectEnable() {
    if (!this.group || this.connectEnableSubmitting) return;
    this.connectEnableSubmitting = true;
    const userIds = Array.from(this.connectEnableSelectedIds);
    const userRoles: Record<string, string> = {};
    for (const id of userIds) {
      userRoles[id] = this.connectUserRoles[id] ?? 'viewer';
    }
    const payload: any = { userIds, userRoles };
    if (this.connectShareSubscription) {
      payload.shareSubscription = true;
      payload.billingDayOfMonth = this.connectBillingDayOfMonth;
    }
    this.subscriptionService.enableConnectForGroup(this.group.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.connectEnableSubmitting = false;
          this.dialog.closeAll();
          this.groupService.patchSelectedGroup({
            connectEnabled: true,
            connectProvisionStatus: 'pending',
            connectProvisionError: null,
            connectProvisionRequestedAt: new Date().toISOString(),
          });
          this.groupService.getGroupUsers().subscribe(() => {});
        },
        error: async (err) => {
          this.connectEnableSubmitting = false;
          if (err?.error?.code === 'PROVIDER_MIGRATION_REQUIRED') {
            this.dialog.closeAll();
            const confirmed = await this.showConfirmationDialog(
              'Update card required',
              `${err.error.message}\n\nWould you like to update your card now?`,
            );
            if (confirmed) {
              this.updateCardDetails(() => this.openConnectEnableDialog());
            }
            return;
          }
          this.showToastMessage(err?.error?.message || 'Failed to enable Connect.');
        }
      });
  }

  requestSupport() {
    if (this.supportRequested || this.requestingSupport) {
      return;
    }

    this.requestingSupport = true;
    this.groupService.inviteUser('contact@medicai.co.za', 'Admin', 'MedicAI Support', true, true, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.supportRequested = true;
          this.requestingSupport = false;
          this.showToastMessage('Support request submitted successfully. Our team will be in touch soon!');
        },
        (error) => {
          this.requestingSupport = false;
          this.showToastMessage('An error occurred submitting the support request. Please try again.');
          console.error('Error submitting support request:', error);
        }
      );
  }

  openWhatsAppSupport(): void {
    const user = this.userService.user;
    if (!user || !this.group || this.openingWhatsAppSupport) {
      return;
    }
    this.openingWhatsAppSupport = true;
    this.supportService.openWhatsAppSupport(user, this.group)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.openingWhatsAppSupport = false;
        },
        error: () => {
          this.openingWhatsAppSupport = false;
          this.showToastMessage('Failed to open WhatsApp support. Please try again.');
        }
      });
  }

  async openInvoiceDetailsDialog(ignoreUpdate: boolean = false): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.dialog.closeAll();
      
      // Reset promo code fields when opening dialog
      this.promoCode = '';
      this.promoCodeDetails = null;
      this.validatingPromoCode = false;
      this.promoCodeError = false;
      
      const dialogRef = this.dialog.open(this.invoiceDetailsDialog!, { width: '400px', autoFocus: false });

      const sub = dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (ignoreUpdate) {
            resolve(result);
            return;
          }
          // If not ignoring update, update invoice details and resolve
          this.updateInvoiceDetails(false);
          resolve(result);
        } else {
          sub.unsubscribe();
          reject();
        }
        sub.unsubscribe();
      });
      
      // Store dialog reference for saveInvoiceDetails
      (this as any).currentInvoiceDialogRef = dialogRef;
    });
  }

  updateCardDetails(onUpdated?: () => void) {
    if (!this.group) return;
    this.processingSubscription = true;
    this.subscriptionService.processCardUpdate(this.group.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.processingSubscription = false;
          if (result.success) {
            this.showToastMessage('Card details updated successfully.');
            this.dialog.closeAll();
            const user = this.userService.user;
            this.groupService.getUserGroups(user.id, user.userType).subscribe();
            onUpdated?.();
          } else if ((result as any).cancelled) {
            this.showToastMessage('Card update cancelled.');
          } else {
            this.showToastMessage('There was an error updating your card. Please try again.');
          }
        },
        error: () => {
          this.processingSubscription = false;
          this.showToastMessage('There was an error updating your card. Please try again.');
        },
      });
  }

  async cancelSubscription() {
    const confirmed = await this.showConfirmationDialog('Cancel Subscription?', 'Are you sure you want to cancel your subscription? You will lose access to MedicAI features for this group until you resubscribe.');
    if (!confirmed) return;

    const cancelSub = this.subscriptionService.cancelSubscription(this.group.id)
      .pipe(tap((response: any) => response.success ? this.groupService.setSelectedGroup(response.group) : null))
      .subscribe(
        (response) => {
          this.showToastMessage('Subscription cancelled successfully');
          cancelSub.unsubscribe();
          this.dialog.closeAll();
        },
        (error) => {
          console.error('Error cancelling subscription:', error);
          this.showToastMessage('There was an error cancelling your subscription. Please contact us for assistance.');
          cancelSub.unsubscribe();
        });
  }

  async upgradeDowngradeSubscription(tier: 'standard' | 'premium') {
    const action = tier === 'premium' ? 'upgrade' : 'downgrade';
    const message = tier === 'premium' 
      ? 'Are you sure you want to upgrade to Premium? You will be charged R6000/month starting from your next billing cycle, and you will receive unlimited AI tokens and text message credits.'
      : 'Are you sure you want to downgrade to Standard? You will be charged R3000/month starting from your next billing cycle, and you will need to purchase AI tokens and text message credits as you use them.';
    
    const confirmed = await this.showConfirmationDialog(`${action.charAt(0).toUpperCase() + action.slice(1)} Subscription?`, message);
    if (!confirmed) return;

    const upgradeSub = this.subscriptionService.upgradeDowngradeSubscription(this.group.id, tier)
      .pipe(tap((response: any) => response.success ? this.groupService.setSelectedGroup(response.group) : null))
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.showToastMessage(`Subscription ${action}d successfully`);
            this.dialog.closeAll();
            upgradeSub.unsubscribe();
          } else {
            this.showToastMessage(`There was an error ${action}ing your subscription. Please try again.`);
            upgradeSub.unsubscribe();
          }
        },
        (error) => {
          console.error(`Error ${action}ing subscription:`, error);
          this.showToastMessage(`There was an error ${action}ing your subscription. Please contact us for assistance.`);
          upgradeSub.unsubscribe();
        });
  }


  openTokenPurchaseDialog() {
    const dialogRef = this.dialog.open(this.tokenPurchaseDialog!, { width: '400px' });
    const sub = dialogRef.afterClosed().subscribe(recharge => {
      if (recharge) {
        const rechargeSub = this.subscriptionService.processTokenRecharge(this.group.id, recharge.amount)
          .subscribe(
            (response) => {
              this.showToastMessage('Token balance updated successfully');
              rechargeSub.unsubscribe();
            },
            (error) => {
              console.error('Error processing your token recharge request:', error);
              this.showToastMessage('There was an error processing your token recharge request. Please try again.');
              rechargeSub.unsubscribe();
            });
      }
      sub.unsubscribe();
    });
  }

  openTextMessageCreditPurchaseDialog() {
    const dialogRef = this.dialog.open(this.textMessageCreditPurchaseDialog!, { width: '400px' });
    const sub = dialogRef.afterClosed().subscribe(recharge => {
      if (recharge) {
        const rechargeSub = this.subscriptionService.processTextMessageCreditRecharge(this.group.id, recharge.amount)
          .subscribe(
            (response) => {
              this.showToastMessage('Text message credit balance updated successfully');
              rechargeSub.unsubscribe();
            },
            (error) => {
              console.error('Error processing your text message credit recharge request:', error);
              this.showToastMessage('There was an error processing your text message credit recharge request. Please try again.');
              rechargeSub.unsubscribe();
            });
      }
      sub.unsubscribe();
    });
  }

  openAutoRechargeDialog() {
    const dialogRef = this.dialog.open(this.autoRechargeDialog!, { width: '400px', autoFocus: false });
    const sub = dialogRef.afterClosed().subscribe(settings => {
      if (settings) {
        const updateSub = this.subscriptionService.updateAutoRechargeSettings(this.group.id, settings)
          .pipe(tap((response: any) => response.success ? this.groupService.setSelectedGroup(response.group) : null))
          .subscribe(
            (response) => {
              this.showToastMessage('Auto recharge settings updated successfully');
              updateSub.unsubscribe();
            },
            (error) => {
              console.error('Error updating auto recharge settings:', error);
              this.showToastMessage('There was an error updating auto recharge settings. Please try again.');
              updateSub.unsubscribe();
            });
      }
      sub.unsubscribe();
    })
  }

  openTextMessageAutoRechargeDialog() {
    const dialogRef = this.dialog.open(this.textMessageAutoRechargeDialog!, { width: '400px', autoFocus: false });
    const sub = dialogRef.afterClosed().subscribe(settings => {
      if (settings) {
        const updateSub = this.subscriptionService.updateTextMessageAutoRechargeSettings(this.group.id, settings)
          .pipe(tap((response: any) => response.success ? this.groupService.setSelectedGroup(response.group) : null))
          .subscribe(
            (response) => {
              this.showToastMessage('Auto recharge settings updated successfully');
              updateSub.unsubscribe();
            },
            (error) => {
              console.error('Error updating auto recharge settings:', error);
              this.showToastMessage('There was an error updating auto recharge settings. Please try again.');
              updateSub.unsubscribe();
            });
      }
      sub.unsubscribe();
    })
  }

  onPromoCodeInput() {
    // Clear error state when user modifies the promo code
    // This allows them to try again after entering an invalid code
    if (this.promoCodeError) {
      this.promoCodeError = false;
      this.promoCodeDetails = null;
    }
  }

  async validatePromoCode() {
    if (!this.promoCode || !this.promoCode.trim()) {
      this.promoCodeDetails = null;
      this.promoCodeError = false;
      this.validatingPromoCode = false;
      return;
    }

    this.validatingPromoCode = true;
    this.promoCodeError = false;
    try {
      const user: any = this.userService.user;
      const response: any = await this.subscriptionService.validatePromoCode(
        this.promoCode.trim(),
        user?.speciality
      ).toPromise();
      
      if (response.valid) {
        this.promoCodeDetails = {
          id: response.promoCode.id,
          discountAmount: response.promoCode.discountAmount,
          discountType: response.promoCode.discountType,
          trialExtensionDays: response.promoCode.trialExtensionDays
        };
        this.promoCodeError = false;
      } else {
        this.promoCodeDetails = null;
        this.promoCodeError = true;
        // Only show error message if code was actually entered
        if (this.promoCode.trim().length > 0) {
          this.showToastMessage(response.message || 'Invalid promo code');
        }
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      this.promoCodeDetails = null;
      this.promoCodeError = true;
      this.showToastMessage('Error validating promo code. Please try again.');
    } finally {
      this.validatingPromoCode = false;
    }
  }

  saveInvoiceDetails() {
    if (!this.invoiceDetails.invoiceName) {
      return;
    }
    
    // Prevent saving if there's an invalid promo code entered
    if (this.promoCode && this.promoCode.trim().length > 0 && this.promoCodeError) {
      this.showToastMessage('Please enter a valid promo code or remove it before proceeding.');
      return;
    }
    
    // Return both invoice details and promo code details
    const result = {
      invoiceDetails: this.invoiceDetails,
      promoCodeDetails: this.promoCodeDetails
    };
    
    // Close the dialog with the result
    const dialogRef = (this as any).currentInvoiceDialogRef;
    if (dialogRef) {
      dialogRef.close(result);
    }
  }

  updateInvoiceDetails(closeDialog: boolean = false) {
    const updateSub = this.subscriptionService.updateInvoiceDetails(this.group.id, this.invoiceDetails)
      .pipe(tap((response: any) => response.success ? this.groupService.setSelectedGroup(response.group) : null))
      .subscribe(
        (response) => {
          this.showToastMessage('Invoice details updated successfully');
          updateSub.unsubscribe();
          if (closeDialog) {
            this.dialog.closeAll();
          }
        },
        (error) => {
          console.error('Error updating invoice details:', error);
          this.showToastMessage('There was an error updating your invoice details. Please try again.');
          updateSub.unsubscribe();
        });
  }

  async purchaseSubscription() {
    const dialogResult = await this.openInvoiceDetailsDialog(true);
    if (!dialogResult) return;

    const { invoiceDetails, promoCodeDetails } = dialogResult;
    this.invoiceDetails = invoiceDetails;
    this.processingSubscription = true;

    const wasNewSubscription = this.isNewSubscription;
    this.subscriptionService.processNewSubscription(invoiceDetails, promoCodeDetails, this.selectedSubscriptionTier)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.processingSubscription = false;
          if (result.success) {
            const user = this.userService.user;
            this.groupService.getUserGroups(user.id, user.userType).subscribe(() => {
              this.showToastMessage('Thank you for subscribing to MedicAI. Your subscription has been processed successfully.');
              this.subscriptionService.handleSubscriptionResponse(true, 'success');
              this.isNewSubscription = false;
              this.dialog.closeAll();
              if (wasNewSubscription) {
                setTimeout(() => this.openOnboardingDialog(), 500);
              }
            });
          } else if ((result as any).cancelled) {
            this.showToastMessage('Your subscription payment was cancelled. Please subscribe to continue.');
            this.subscriptionService.handleSubscriptionResponse(true, 'cancelled');
            this.dialog.closeAll();
          } else {
            this.showToastMessage('An error occurred processing your subscription. Please try again or contact support.');
            this.subscriptionService.handleSubscriptionResponse(false, 'error');
            this.dialog.closeAll();
          }
        },
        error: () => {
          this.processingSubscription = false;
          this.showToastMessage('An error occurred processing your subscription. Please try again or contact support.');
          this.subscriptionService.handleSubscriptionResponse(false, 'error');
          this.dialog.closeAll();
        },
      });
  }

  loadInvoices() {
    if (this.invoicesLoaded || !this.group) {
      return;
    }
    this.invoicesLoaded = true;
    this.subscriptionService.getInvoicesByGroup(this.group.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Invoices are already set via the invoices$ subscription
        },
        error: (error) => {
          console.error('Error loading invoices:', error);
          this.invoicesLoaded = false;
        }
      });
  }

}
